import React from "react"
import ReactDOM from "react-dom"
import { ApolloConsumer } from "react-apollo"
import BIError from "../../BIError"
import Loading from "../../Loading"
import GET_ARTICLE_EMBED_TOKEN from "../../../graphql/getArticleEmbedToken.graphql"
import PropTypes from "prop-types"

import "./powerBI.scss"

class PowerBIComponent extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      loading: false,
      error: null,
    }
  }

  componentDidMount = () => {
    this.pbi = require("powerbi-client")
    this.embedElement = ReactDOM.findDOMNode(this.refs.embed)
    this.fetchTokenAndEmbed()
  }

  isTokenValid = (embedToken) => {
    return (
      embedToken &&
      embedToken.token &&
      embedToken.expiration &&
      new Date(embedToken.expiration) > new Date()
    )
  }

  fetchTokenAndEmbed = async () => {
    if (!this.pbi) return

    let { fetchToken } = this.props

    let reportItem = `article.${this.props.reportID}`.toLowerCase()

    this.setState({ loading: true })

    let embedToken = localStorage.getItem(reportItem)

    embedToken = embedToken ? JSON.parse(embedToken) : {}

    if (!this.isTokenValid(embedToken)) {
      let response = await fetchToken()

      if (response.error) {
        this.setState({
          loading: false,
          error: response.error,
        })
        return
      }

      embedToken = response.data.articleEmbedToken

      localStorage.setItem(reportItem, JSON.stringify(embedToken))

      if (!embedToken || !embedToken.id) {
        this.setState({
          loading: false,
          error: !embedToken ? "Internal server error" : embedToken.token,
        })
        return
      }
    }
    this.embedReport(embedToken)
  }

  embedReport = (embedToken) => {
    if (!this.isTokenValid(embedToken)) {
      this.setState(({ error }) => ({
        loading: false,
        error: error || "Invalid embed token.",
      }))
      return
    }
    let embedConfig = {
      type: "report",
      id: embedToken.id,
      embedUrl: "https://app.powerbi.com/reportEmbed",
      tokenType: this.pbi.models.TokenType.Embed,
      accessToken: embedToken.token,
      settings: {
        filterPaneEnabled: false,
        navContentPaneEnabled: true,
      },
    }
    powerbi.reset(this.embedElement)
    this.report = powerbi.embed(this.embedElement, embedConfig)
    this.setState({ loading: false })
    // Define event handlers for Power BI report
    this.report.on("error", this.onReportError)
    this.report.on("dataSelected", this.onDataSelected)
  }

  onDataSelected = (e) => {
    let { dataPoints, visual } = e.detail
    let point = dataPoints[dataPoints.length - 1].identity[0]

    this.props.trackEvent("Selected data", {
      article: this.props.articleSlug || "",
      title: this.props.modelTitle,
      dataTable: point.target.table,
      dataColumn: point.target.column,
      dataValue: point.equals,
      dataVisual: visual.title || visual.name,
    })
  }

  onReportError = (event) => {
    this.setState({
      loading: false,
      error: event.detail,
    })
  }

  render = () => {
    if (this.state.error) {
      return <BIError />
    }
    return (
      <section className="powerbi-component">
        <h2 className="powerbi-component--title">{this.props.modelTitle}</h2>
        <p className="powerbi-component--description">
          {this.props.modelDescription}
        </p>
        <div className="powerbi-component--embed" ref="embed" />
        {this.state.loading && <Loading />}
      </section>
    )
  }
}

PowerBIComponent.propTypes = {
  workspaceID: PropTypes.string,
  reportID: PropTypes.string,
  modelTitle: PropTypes.string,
  modelDescription: PropTypes.string,
  newsletterVersion: PropTypes.string,
  articleName: PropTypes.string,
}

PowerBIComponent.defaultProps = {
  workspaceID: "",
  reportID: "",
  modelTitle: "",
  modelDescription: "",
  newsletterVersion: "",
  articleName: "",
}

const PowerBIComponentWithData = (props) => (
  <ApolloConsumer>
    {(client) => {
      const fetchToken = () =>
        client.query({
          query: GET_ARTICLE_EMBED_TOKEN,
          fetchPolicy: "network-only",
          variables: {
            workspaceId: props.workspaceID,
            reportId: props.reportID,
          },
        })

      return <PowerBIComponent {...props} fetchToken={fetchToken} />
    }}
  </ApolloConsumer>
)

export default PowerBIComponentWithData
